import React, { lazy, Suspense } from "react";
import FacelensApp1 from "@/assets/facelens/waggex-facelens-biometric-machine-problem-solution.webp?w=350;440&format=webp";
import FacelensApp2 from "@/assets/facelens/facelens2.webp?w=400;600;800&format=webp";
import mobile from "@/assets/facelens/waggex-facelens-app-face-recognition-attendance-india.webp?w=360;420&format=webp";
import StatsSection from "./common/StatsSection";
import {
  ScanFace,
  Clock3,
  UserRoundCheck,
  ReceiptText,
  DoorOpen,
  TrendingUp,
  Check,
  ClipboardList,
  MapPinned,
  Smartphone,
  FileText,
  DollarSign,
} from "lucide-react";
import { useInView } from "react-intersection-observer";
const StaffAppFeatures = lazy(() => import("./staffapp/StaffAppFeatures"));
const CustomerReviews = lazy(
  () => import("../components/payrolewebapp/CustomerReviews"),
);

const ThreeSteps = lazy(() => import("./ThreeSteps"));

const ComparisonTable = lazy(
  () => import("../components/payrolewebapp/ComparisonTable"),
);

const HomeFaq = lazy(() => import("../pages/HomeFaq"));

const AppDownloadCard = lazy(
  () => import("../components/common/AppDownloadCard"),
);

const PayrollBanner = lazy(() => import("../components/common/PayrollBanner"));

const ModernizeHR = lazy(() => import("../components/common/ModernizeHR"));
import { Helmet } from "react-helmet-async";
import { useHrmsCounts } from "../hooks/useHrmsCounts";

const FacelensFaqs1 = [
  {
    title: "What is FaceLens App?",
    content:
      "FaceLens is Waggex's AI-powered face recognition attendance app that verifies employee identity through mobile face identification. Employees can mark attendance using any Android device without biometric machines, fingerprint scanners, or shared hardware.",
  },
  {
    title: "Does FaceLens replace a biometric attendance system?",
    content:
      "Yes. FaceLens replaces traditional biometric attendance systems with AI face recognition technology on mobile devices. It provides accurate identity verification along with GPS location tagging at a significantly lower hardware cost.",
  },
  {
    title: "Does FaceLens support GPS attendance verification?",
    content:
      "Yes. GPS coordinates are captured and stored with every attendance check-in. This verifies both employee identity and the exact work location, making it suitable for office, remote, and field teams.",
  },
];

const FacelensFaqs2 = [
  {
    title: "Can remote and field employees use FaceLens App?",
    content:
      "Yes. FaceLens is designed for remote and field-based employees including sales teams, delivery staff, and service engineers. Employees can mark attendance directly from their Android phone using a GPS-tagged selfie.",
  },
  {
    title: "Is FaceLens suitable for small businesses in India?",
    content:
      "Yes. FaceLens is scalable for startups, SMEs, and large enterprises ranging from 10 to 1,000+ employees. Businesses benefit from AI face recognition, GPS verification, and payroll integration without investing in biometric hardware.",
  },
  {
    title: "Does FaceLens face recognition attendance work across India?",
    content:
      "Yes. FaceLens works across all cities, states, and remote regions in India through mobile GPS technology. As long as the employee device has camera and GPS access enabled, attendance tracking remains active and accurate.",
  },
];

const facelensFeatures = [
  {
    icon: <ScanFace size={24} aria-hidden="true" />,
    title: "AI Face Recognition",
    desc: "AI face detection identifies the registered employee accurately before every attendance entry is saved.",
  },
  {
    icon: <Smartphone size={24} aria-hidden="true" />,
    title: "Use Any Device",
    desc: "Works on any Android phone or tablet already in use, no additional hardware purchase required.",
  },
  {
    icon: <Clock3 size={24} aria-hidden="true" />,
    title: "Fast Check-In",
    desc: "Attendance verification completes in under three seconds so employees are in and out without any queue.",
  },
  {
    icon: <UserRoundCheck size={24} aria-hidden="true" />,
    title: "Employee Verification",
    desc: "Liveness detection ensures only a verified live face is accepted; photos and videos are rejected.",
  },
  {
    icon: <ReceiptText size={24} aria-hidden="true" />,
    title: "Payroll Connected",
    desc: "Verified attendance syncs directly with Waggex payroll for accurate and error-free salary processing.",
  },
  {
    icon: <FileText size={24} aria-hidden="true" />,
    title: "Easy HR Forms",
    desc: "Attendance data populates HR reports, statutory forms, and leave records automatically without manual entry.",
  },
  {
    icon: <DoorOpen size={24} aria-hidden="true" />,
    title: "Office Entry Point",
    desc: "Install as a shared kiosk at office or site entrance; multiple employees can check in from the same device.",
  },
  {
    icon: <TrendingUp size={24} aria-hidden="true" />,
    title: "Built for Growth",
    desc: "Scales from 10 employees to 1,000, no hardware upgrade needed as your business expands.",
  },
];
const facelensSteps = [
  {
    number: "1",
    title: "Register Employees",
    description:
      "Add employees and enrol their face profiles securely inside FaceLens.",
  },
  {
    number: "2",
    title: "Install FaceLens",
    description:
      "Install on any Android phone, tablet, or shared office entry device.",
  },
  {
    number: "3",
    title: "Employee Scans Face",
    description:
      "Employee stands in front of the camera and AI verifies identity instantly.",
  },
  {
    number: "4",
    title: "Attendance Marked",
    description:
      "Verified attendance record is created and synced to the HR dashboard.",
  },
  {
    number: "5",
    title: "HR Reviews Data",
    description:
      "Managers access attendance reports, late entries, and work logs anytime.",
  },
];
const headers = ["FEATURES", "BIOMETRIC MACHINE", "FACELENS APP"];

const rows = [
  ["Contactless Attendance", "No", "Yes"],
  ["Mobile Attendance", "No", "Yes"],
  ["Face Recognition", "No", "Yes"],
  ["GPS Verification", "No", "Yes"],
  ["Real-Time Reports", "Limited", "Yes"],
  ["Hardware Required", "Yes", "No"],
  ["Field Employee Support", "Limited", "Yes"],
  ["Remote Team Ready", "No", "Yes"],
];
const problems = [
  {
    icon: ClipboardList,
    title: "Manual Attendance Errors",
    description:
      "Manual registers are inaccurate, easily manipulated, and create payroll discrepancies every month.",
  },
  {
    icon: DollarSign,
    title: "High Hardware Cost",
    description:
      "Biometric terminals cost ₹8,000–₹40,000 per device and require regular servicing and replacement.",
  },
  {
    icon: FileText,
    title: "Payroll Mistakes",
    description:
      "Manual attendance errors cascade directly into incorrect salary calculations, deductions, and statutory filings.",
  },
  {
    icon: MapPinned,
    title: "Multi-Location Issues",
    description:
      "Biometric machines are fixed at one location; field teams, remote staff, and branch employees cannot be managed.",
  },
];

const schema = [
  // SCHEMA 1: SoftwareApplication
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "FaceLens by Waggex",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Android, iOS",
    description:
      "FaceLens is an AI face recognition attendance app by Waggex for Indian businesses. Contactless face identification, GPS verification, and direct payroll sync with no biometric hardware required.",
    url: "https://www.waggex.com/facelens-app",
    screenshot:
      "https://www.waggex.com/waggex-facelens-app-face-recognition-attendance-india.webp",
    featureList: [
      "Face recognition attendance app",
      "Face identification app India",
      "Contactless attendance app India",
      "AI face recognition attendance",
      "GPS attendance verification",
      "Replace biometric machine India",
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "298",
      bestRating: "5",
      worstRating: "1",
    },
  },

  // SCHEMA 2: FAQPage
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is FaceLens App?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "FaceLens is Waggex's AI face recognition attendance app that verifies employee identity through mobile face scanning without biometric hardware.",
        },
      },
      {
        "@type": "Question",
        name: "Does FaceLens replace a biometric attendance system?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. FaceLens replaces biometric machines with AI face recognition and GPS-based verification on mobile devices.",
        },
      },
      {
        "@type": "Question",
        name: "Does FaceLens support GPS attendance verification?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Every check-in includes GPS coordinates to verify employee location along with identity confirmation.",
        },
      },
      {
        "@type": "Question",
        name: "Can remote and field employees use FaceLens App?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Field staff and remote employees can mark attendance from any Android device with GPS and camera access.",
        },
      },
      {
        "@type": "Question",
        name: "Is FaceLens suitable for small businesses in India?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. FaceLens is scalable from small teams to large enterprises without hardware dependency.",
        },
      },
      {
        "@type": "Question",
        name: "Does FaceLens work across India?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. It works across all cities and regions in India as long as the device has GPS and internet access.",
        },
      },
    ],
  },

  // SCHEMA 3: Organization
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides AI-powered face recognition attendance and complete HRMS for Indian businesses.",
    areaServed: "IN",
  },
];
export default function FaceLens() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { counts, loading } = useHrmsCounts(inView);
  return (
    <>
      {/* <SEO
        title="Face Recognition Attendance System for Employees | Waggex Face Lens"
        description="Track employee attendance with Waggex Face Lens — a smart face recognition attendance system. Install on any device and enable secure, real-time attendance tracking with automated payroll accuracy."
        keywords="face recognition attendance, system employee attendance system attendance software India biometric attendance alternative attendance app payroll attendance system"
        canonical="https://www.waggex.com/facelens-app"
      /> */}
      <Helmet>
        <title>
          Face Recognition Attendance App | No Biometric Device Needed
        </title>
        <meta
          name="description"
          content="FaceLens by Waggex replaces biometric machines with AI face attendance using employee phones, GPS verification, and payroll integration."
        />
        <meta
          name="keywords"
          content="face recognition attendance app,face identification app,face recognition app India,AI face recognition attendance,contactless attendance app India,face recognition biometric attendance,mobile attendance app face recognition,replace biometric machine app India"
        />
        <link rel="canonical" href="https://www.waggex.com/facelens-app" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <section className="w-full bg-[#d1dfea] overflow-hidden py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-10">
            {/* Left Content */}
            <div className="text-center lg:text-left min-h-130">
              {/* Badge */}
              <div className="inline-block border border-[#1d3b5c] rounded-xl px-5 py-2 text-[#1d3b5c] text-xs sm:text-sm font-medium">
                Face Lens App
              </div>

              {/* Heading */}
              <h1 className="mt-8 text-[#10324F] font-bold uppercase tracking-wide text-xl md:text-[27px]">
                AI-Powered Face Recognition Attendance App
              </h1>

              {/* Description */}
              <div className="face-service-list-item">
                <p className="face-service-list-item-text py-4 min-h-38">
                  Replace fingerprint machines and paper registers with
                  FaceLens, an AI face recognition attendance app built for
                  modern Indian businesses. Verify employee identity using face
                  identification technology with check-ins and automatic payroll
                  integration every day
                </p>
              </div>
              <h2 className="text-[#10324F] font-roboto font-bold text-sm sm:text-lg">
                AI Face Recognition Attendance for Modern Workplaces
              </h2>

              {/* Description */}
              <p className="face-service-list-item-text py-4">
                FaceLens uses AI face recognition technology to verify employee
                attendance on any Android device. No fingerprint scanner, no
                manual registers, no expensive biometric hardware, just
                accurate, contactless, and fast face identification for every
                attendance check-in
              </p>

              {/* Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() =>
                    (window.location.href = "https://app.waggex.com/signup")
                  }
                  className="w-full sm:w-auto bg-[#16395f] hover:bg-[#0f2d4c] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
                >
                  Start Free Trial
                </button>

                <button
                  onClick={() => (window.location.href = "/contact-us")}
                  className="w-full sm:w-auto border border-[#1d3b5c] text-[#1d3b5c] hover:bg-[#1d3b5c] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
                >
                  Book a Demo
                </button>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative flex justify-center items-center min-h-100">
              {/* Phones Image */}
              {/* <img
                src={mobile[0]}
                width={382}
                height={380}
                fetchPriority="high"
                loading="eager"
                alt="FaceLens App showing AI face recognition attendance on Android phone no biometric hardware required India"
                className="relative object-contain block sm:hidden"
              /> */}
              <picture>
                <source media="(max-width: 639px)" srcSet={mobile[0]} />
                <img
                  src={mobile[1]}
                  width={420}
                  height={417}
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  alt="FaceLens App showing AI face recognition attendance on Android phone no biometric hardware required India"
                  className="relative object-contain"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>
      <section ref={ref}>
        <StatsSection counts={counts} loading={loading} />
      </section>
      <section className="w-full bg-white px-6 py-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-6 text-center lg:text-left">
              <h2 className="text-[#10324F] text-xl sm:text-3xl font-roboto font-bold leading-snug">
                Still Using Manual Attendance or Biometric Machines?
              </h2>

              <p className="selfie-service-list-item-text py-4">
                Biometric machines require expensive procurement, break down
                under heavy use, and timely maintenance. FaceLens delivers
                AI-powered face identification on mobile accurate, contactless,
                and with no extra hardware investment.
              </p>

              <button
                onClick={() =>
                  (window.location.href = "https://app.waggex.com/signup")
                }
                className="mt-4 bg-[#10324F] text-white h-12.5 px-14 rounded-xl text-xl shadow-lg hover:scale-[1.02] transition"
              >
                Get Started with FaceLens
              </button>
            </div>

            {/* Right Cards */}
            <div className="space-y-5">
              {problems.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    style={{
                      boxShadow: `
                  rgb(167 191 210 / 40%) -15px -15px 20px inset,
                  rgb(170 188 202 / 30%) -20px -20px 30px
                `,
                    }}
                    className="h-36 sm:h-30 flex gap-4 items-start bg-white border border-[#E5EDF5] rounded-2xl p-5 shadow-sm hover:shadow-md transition-all duration-300 max-w-xl"
                  >
                    {/* Icon */}
                    <div className="w-14 h-14 shrink-0 rounded-xl border border-[#DCE7F2] bg-[#F8FBFF] flex items-center justify-center">
                      <Icon
                        className="w-6 h-6 text-[#10324F]"
                        aria-hidden="true"
                      />
                    </div>

                    {/* Content */}
                    <div>
                      <h2 className="text-[#10324F] font-semibold text-sm">
                        {item.title}
                      </h2>

                      <p className="selfie-service-list-item-text mt-1">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      {/* Row 2 */}
      <section className="FacelensApp bg-[#f6f9fb] w-full px-6 py-10">
        <div className="max-w-6xl mx-auto ">
          <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1 flex justify-center">
              {/* <img
                src={FacelensApp2} */}
              <img
                src={FacelensApp1[0]}
                width="364"
                height="256"
                fetchPriority="high"
                alt="FaceLens App replacing biometric attendance machine with AI face recognition for Indian businesses"
                className="object-contain block sm:hidden"
              />
              <img
                src={FacelensApp1[1]} // fallback image
                // srcSet={`${FacelensApp1[0]} 400w,${FacelensApp1[1]} 600w,${FacelensApp1[2]} 800w`}
                sizes="(max-width: 640px) 100vw"
                width="400"
                height="281"
                fetchPriority="high"
                alt="FaceLens App replacing biometric attendance machine with AI face recognition for Indian businesses"
                className="object-contain hidden sm:block"
              />
            </div>

            <div className="order-1 md:order-2 space-y-4">
              <div className="payroll-section-content-width">
                <h2 className="service-section-heading">
                  Why Choose FaceLens Face Recognition App?
                </h2>

                <ul className="space-y-3 mt-3">
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <div>
                      <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                        Use Phones Instead of Biometric Machines
                      </h2>
                      <p className="text-xs sm:text-sm text-slate-600 mt-1">
                        No expensive biometric devices or fingerprint scanners
                        required. Employees can mark attendance directly from
                        their own Android phones using AI face recognition
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <div>
                      <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                        AI Face Verification
                      </h2>
                      <p className="text-xs sm:text-sm text-slate-600 mt-1">
                        Advanced face recognition ensures only the actual
                        employee can check in, helping prevent proxy attendance
                        and buddy punching
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <div>
                      <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                        Real-Time Attendance Tracking
                      </h2>
                      <p className="text-xs sm:text-sm text-slate-600 mt-1">
                        Monitor employee attendance instantly with live updates,
                        location logs, and centralized reports from a single
                        dashboard
                      </p>
                    </div>
                  </li>

                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <div>
                      <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                        Works Anywhere, Anytime
                      </h2>
                      <p className="text-xs sm:text-sm text-slate-600 mt-1">
                        Perfect for offices, remote teams, field staff,
                        warehouses, construction sites, and multi-location
                        businesses across India
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3 items-start">
                    <div className="shrink-0 w-5.25 h-6.25 flex items-center justify-center">
                      <div className="diamond-icon-sm"></div>
                    </div>
                    <div>
                      <h2 className="font-semibold text-[#10324F] text-sm sm:text-base">
                        Touchless & Hygienic Attendance
                      </h2>
                      <p className="text-xs sm:text-sm text-slate-600 mt-1">
                        Employees mark attendance without touching shared
                        biometric machines, making the process faster, safer,
                        and more hygienic
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Suspense fallback={null}>
        <StaffAppFeatures
          title="Powerful Features of FaceLens App"
          subtitle="FaceLens is designed to simplify employee attendance verification and connect attendance data with payroll operations."
          features={facelensFeatures}
        />
        <ThreeSteps
          title="How FaceLens App Works"
          subtitle="FaceLens is designed to simplify employee attendance verification and connect attendance data with payroll operations."
          steps={facelensSteps}
          backcolor="#FFFFFF"
        />

        <ComparisonTable
          headers={headers}
          rows={rows}
          title="FaceLens vs Traditional Attendance Systems"
          subtitle="Compare FaceLens with manual attendance and traditional biometric machines."
        />

        {/* reviews */}
        <CustomerReviews />

        <HomeFaq
          faq1={FacelensFaqs1}
          faq2={FacelensFaqs2}
          subtitle="Everything you need to know about Waggex payroll software in India"
        />
        {/*  <AppDownloadCard /> */}
        <PayrollBanner />
        <AppDownloadCard />
        <ModernizeHR
          heading="Upgrade From Biometric Machines to AI Face Recognition"
          description="Manage attendance smarter with FaceLens a modern AI face recognition attendance app designed for Indian businesses that require accurate, contactless, and fraud-proof employee verification."
        />
      </Suspense>
    </>
  );
}
